import {getAuthToken} from '@/utils/auth-token';
import store from '@/store';

let refreshPromise = null;

const hasNavigator = function () {
    return typeof navigator !== 'undefined';
};

const canSetBadge = function () {
    return hasNavigator() && typeof navigator.setAppBadge === 'function';
};

const canClearBadge = function () {
    return hasNavigator() && typeof navigator.clearAppBadge === 'function';
};

const clearPwaBadge = async function () {
    if (canClearBadge()) {
        await navigator.clearAppBadge();
        return;
    }

    if (canSetBadge()) {
        await navigator.setAppBadge(0);
    }
};

const applyPwaBadgeCount = async function (count) {
    const normalizedCount = Math.max(0, Number(count || 0));
    if (normalizedCount < 1) {
        await clearPwaBadge();
        return;
    }

    if (!canSetBadge()) {
        return;
    }

    await navigator.setAppBadge(Math.floor(normalizedCount));
};

const syncTicketBadgeState = function (payload) {
    if (!store || typeof store.commit !== 'function') {
        return;
    }

    store.commit('setTicketBadge', {
        enabled: !!payload?.enabled,
        count: Math.max(0, Number(payload?.count || 0)),
        source: payload?.source ? String(payload.source) : null,
    });
};

export async function refreshPwaBadgeCount() {
    if (refreshPromise) {
        return refreshPromise;
    }

    refreshPromise = (async function () {
        if (typeof window === 'undefined' || typeof axios === 'undefined') {
            syncTicketBadgeState({enabled: false, count: 0, source: null});
            if (canSetBadge() || canClearBadge()) {
                await clearPwaBadge();
            }
            return {enabled: false, count: 0};
        }

        if (!getAuthToken()) {
            syncTicketBadgeState({enabled: false, count: 0, source: null});
            if (canSetBadge() || canClearBadge()) {
                await clearPwaBadge();
            }
            return {enabled: false, count: 0};
        }

        try {
            const response = await axios.get('api/account/pwa-badge');
            const source = String(response?.data?.source || '');
            const count = Math.max(0, Number(response?.data?.count || 0));
            const pwaEnabled = !!response?.data?.pwa_enabled;

            if (source !== 'unread_tickets') {
                syncTicketBadgeState({enabled: false, count: 0, source});
                if (canSetBadge() || canClearBadge()) {
                    await clearPwaBadge();
                }
                return {enabled: false, count: 0};
            }

            syncTicketBadgeState({enabled: true, count, source});

            if (!pwaEnabled) {
                if (canSetBadge() || canClearBadge()) {
                    await clearPwaBadge();
                }
                return {enabled: false, count};
            }

            if (canSetBadge() || canClearBadge()) {
                await applyPwaBadgeCount(count);
            }

            return {enabled: true, count};
        } catch (_error) {
            syncTicketBadgeState({enabled: false, count: 0, source: null});
            if (canSetBadge() || canClearBadge()) {
                await clearPwaBadge();
            }
            return {enabled: false, count: 0};
        }
    })().finally(function () {
        refreshPromise = null;
    });

    return refreshPromise;
}
